/**
 * StatsView — Review statistics
 *
 * Piccard design matching project tokens:
 * - Today: reviewed today hero number
 * - All time: 3 badges (total reviews, mastered, words)
 */

import { useState, useEffect } from 'react'
import { getAccumulatedStats } from '@/lib/api-client'

interface Stats {
  reviewedToday: number
  totalReviews: number
  totalCards: number
  masteredCards: number
}

export function StatsView() {
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    let cancelled = false
    getAccumulatedStats()
      .then((data) => {
        if (!cancelled) {
          setStats({
            reviewedToday: data.reviewedToday,
            totalReviews: data.totalReviews,
            totalCards: data.totalCards,
            masteredCards: data.masteredCards,
          })
        }
      })
      .catch(() => {
        if (!cancelled) setStats({ reviewedToday: 0, totalReviews: 0, totalCards: 0, masteredCards: 0 })
      })
    return () => { cancelled = true }
  }, [])

  if (!stats) {
    return (
      <div className="flex flex-col items-center px-4 py-6" data-loading>
        <div className="w-full max-w-sm space-y-3 animate-pulse">
          <div className="bg-surface-container-lowest rounded-2xl p-5 shadow-card space-y-3">
            <div className="h-3 w-16 bg-surface-container-low rounded" />
            <div className="mx-auto h-12 w-28 bg-surface-container-low rounded" />
            <div className="mx-auto h-3 w-20 bg-surface-container-low rounded" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-surface-container-lowest rounded-2xl p-3 h-20 shadow-card" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const masteryRate = stats.totalCards > 0
    ? Math.round((stats.masteredCards / stats.totalCards) * 100)
    : 0

  return (
    <div className="flex flex-col items-center px-4 py-6">
      <div className="w-full max-w-sm space-y-3">
        {/* Hero — Reviewed Today */}
        <div className="bg-surface-container-lowest rounded-2xl p-5 shadow-card">
          <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-on-surface-variant/60 mb-1">
            Today
          </p>
          <p className="font-headline font-extrabold text-[44px] leading-none text-ink">
            {stats.reviewedToday}
          </p>
          <p className="text-on-surface-variant text-xs font-medium mt-1.5">reviewed today</p>
        </div>

        {/* 3 Metric Cards */}
        <div className="grid grid-cols-3 gap-3">
          {/* Total Reviews */}
          <div className="bg-surface-container-lowest rounded-2xl p-3 shadow-card flex flex-col items-center">
            <span className="material-symbols-outlined text-accent-amber text-[20px] mb-1">replay</span>
            <p className="font-headline font-extrabold text-xl text-ink leading-none">
              {stats.totalReviews}
            </p>
            <p className="text-[9px] font-bold text-on-surface-variant/60 mt-1">reviews</p>
            <div className="w-full h-1 rounded-full bg-surface-container-low mt-2">
              <div className="h-full rounded-full bg-accent-amber" style={{ width: `${Math.min(100, (stats.totalReviews / 1000) * 100)}%` }} />
            </div>
          </div>

          {/* Mastered */}
          <div className="bg-surface-container-lowest rounded-2xl p-3 shadow-card flex flex-col items-center">
            <span className="material-symbols-outlined text-accent-teal text-[20px] mb-1">emoji_events</span>
            <p className="font-headline font-extrabold text-xl text-ink leading-none">
              {stats.masteredCards}
            </p>
            <p className="text-[9px] font-bold text-on-surface-variant/60 mt-1">mastered</p>
            <div className="w-full h-1 rounded-full bg-surface-container-low mt-2">
              <div className="h-full rounded-full bg-accent-teal" style={{ width: `${masteryRate}%` }} />
            </div>
          </div>

          {/* Total Words */}
          <div className="bg-surface-container-lowest rounded-2xl p-3 shadow-card flex flex-col items-center">
            <span className="material-symbols-outlined text-primary text-[20px] mb-1">menu_book</span>
            <p className="font-headline font-extrabold text-xl text-ink leading-none">
              {stats.totalCards}
            </p>
            <p className="text-[9px] font-bold text-on-surface-variant/60 mt-1">words</p>
            <div className="w-full h-1 rounded-full bg-surface-container-low mt-2">
              <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (stats.totalCards / 500) * 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
